import { useState, type FormEvent } from 'react'
import type { TranscriptSummary } from '../../contract'
import type { Client } from '../api'
import { fmtDate } from '../format'
import { navigate, type Route } from '../router'

interface Props { client: Client; transcripts: TranscriptSummary[]; route: Route; onAdded: () => Promise<void> }

const PLACEHOLDER = `[00:04] Researcher: Thanks for making the time. What does a study look like for you?
[00:11] Participant 9: Officially it begins with a research request. Realistically it begins on a Thursday.`

export function TranscriptsView({ client, transcripts, route, onAdded }: Props) {
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const t = await client.createTranscript(title.trim() || 'Untitled transcript', text)
      await onAdded()
      setAdding(false); setTitle(''); setText('')
      navigate({ view: 'transcripts', r: t.id })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false) }
  }

  async function onFile(f: File | undefined) {
    if (!f) return
    setText(await f.text())
    if (!title) setTitle(f.name.replace(/\.(txt|vtt|md)$/i, ''))
  }

  return (
    <div className="view view--transcripts">
      <div className="view__head">
        <div>
          <h1 className="view__title">Transcripts</h1>
          <p className="view__lede">{transcripts.length} in the corpus. Samples are fictional interviews generated for this demo.</p>
        </div>
        <button className="btn btn--primary" onClick={() => setAdding((a) => !a)} aria-expanded={adding} data-testid="add-transcript">
          {adding ? 'Cancel' : '+ Add transcript'}
        </button>
      </div>

      {adding && (
        <form className="addform card" onSubmit={submit} data-testid="add-form">
          <label className="field">
            <span>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Interview 9 — support lead, logistics" data-testid="add-title" />
          </label>
          <label className="field">
            <span>Transcript <small>· <code>[mm:ss] Speaker: text</code> lines or WebVTT</small></span>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={PLACEHOLDER} required data-testid="add-text" spellCheck={false} />
          </label>
          <div className="addform__row">
            <label className="btn btn--ghost">
              Upload file
              <input type="file" accept=".txt,.vtt,.md,text/plain,text/vtt" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="add-file" />
            </label>
            <span className="grow" />
            {error && <span className="error">{error}</span>}
            <button className="btn btn--primary" disabled={busy || !text.trim()} data-testid="add-submit">{busy ? 'Adding…' : 'Add to corpus'}</button>
          </div>
        </form>
      )}

      <ul className="tlist" data-testid="transcript-list">
        {transcripts.map((t) => (
          <li key={t.id}>
            <a
              className={`tcard${route.r === t.id ? ' tcard--active' : ''}`}
              href={`#transcripts&r=${encodeURIComponent(t.id)}`}
              data-testid="transcript-card"
              aria-current={route.r === t.id ? 'true' : undefined}
            >
              <span className="tcard__top">
                {t.sample && <span className="badge badge--sample" data-testid="sample-badge">Sample</span>}
                <span className="mono tcard__id">{t.id}</span>
              </span>
              <span className="tcard__title">{t.title}</span>
              <span className="tcard__meta">{t.lineCount} lines · {fmtDate(t.createdAt)}</span>
            </a>
          </li>
        ))}
      </ul>
      {transcripts.length === 0 && <p className="empty">No transcripts yet. Paste one above.</p>}
    </div>
  )
}
